import React, { ReactNode } from 'react';
import { FileRejection } from 'react-dropzone';
import styled from 'styled-components';

import { CsvImportSelectedFileCard } from '@/features/csvImport/admin/atoms/CsvImportSelectedFileCard';
import { DropArea } from '@/ui/DropArea';
import { Icon } from '@/ui/Icon';

/** Drops and the file picker both accept only this. Anything else arrives as a rejection. */
const CSV_ACCEPT = { 'text/csv': ['.csv'] };

const RootStyled = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleStyled = styled.h1`
  ${(p) => p.theme.typography.title2};
  /* The legacy global stylesheet still puts em-based margins on headings and paragraphs */
  margin: 0;
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const SubtitleStyled = styled.p`
  ${(p) => p.theme.typography.body2};
  margin: 8px 0 0;
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const TemplateActionStyled = styled.div`
  display: flex;
  margin-top: 12px;
`;

const FileAreaStyled = styled.div`
  display: flex;
  margin-top: 40px;
`;

const DropAreaIconStyled = styled(Icon)`
  font-size: 24px;
  color: ${(p) => p.theme.colors.neutral.outline2};
`;

/* Paragraphs again — the dashed area centres its own children, so only spacing is set here */
const DropAreaHintStyled = styled.p`
  ${(p) => p.theme.typography.caption};
  margin: 8px 0 0;
  color: ${(p) => p.theme.colors.neutral.onContainer};
`;

const DropAreaPromptStyled = styled.p`
  ${(p) => p.theme.typography.body2};
  margin: 16px 0 0;
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

/**
 * Looks like the link it stands in for. It cannot be one: the drop area is itself the
 * control, so a nested button or anchor would be unreachable.
 */
const BrowseStyled = styled.span`
  color: ${(p) => p.theme.colors.primary.main};
`;

type CsvImportUploadProps = {
  title: string;
  subtitle: string;
  /** Control above the drop area, e.g. a "Download template" link. */
  templateAction?: ReactNode;
  /** The staged file, or `null` for the empty drop area. */
  file: File | null;
  /** Data rows counted from `file`. Omitted while the count is still being read. */
  rowCount?: number;
  /** While the file is being validated: keeps it on screen but refuses to let it go. */
  loading?: boolean;
  onFileSelected: (file: File) => void;
  onFileRemoved: VoidFunction;
  /**
   * Files the drop area turned down — wrong extension, or over `maxSize`. The area shows
   * nothing itself, so the flow decides what the admin is told.
   */
  onFilesRejected?: (rejections: FileRejection[]) => void;
  /** Maximum size per file in bytes. Omit to accept any size and let the server judge. */
  maxSize?: number;
  className?: string;
};

/**
 * The upload step both bulk-CSV flows share: what the file is for, where to get the
 * template, and one CSV either waiting to be dropped or staged for validation.
 */
export const CsvImportUpload = ({
  title,
  subtitle,
  templateAction,
  file,
  rowCount,
  loading = false,
  onFileSelected,
  onFileRemoved,
  onFilesRejected,
  maxSize,
  className,
}: CsvImportUploadProps): JSX.Element => (
  <RootStyled className={className}>
    <TitleStyled>{title}</TitleStyled>
    <SubtitleStyled>{subtitle}</SubtitleStyled>
    {!!templateAction && <TemplateActionStyled>{templateAction}</TemplateActionStyled>}
    <FileAreaStyled>
      {file ? (
        <CsvImportSelectedFileCard fileName={file.name} rowCount={rowCount} onRemove={onFileRemoved} removeDisabled={loading} />
      ) : (
        <DropArea
          accept={CSV_ACCEPT}
          maxSize={maxSize}
          disabled={loading}
          onFilesSelected={([selectedFile]) => onFileSelected(selectedFile)}
          onFilesRejected={onFilesRejected}
          aria-label="Upload a CSV file"
        >
          <DropAreaIconStyled icon={['far', 'cloud-arrow-up']} aria-hidden />
          <DropAreaHintStyled>.csv files only</DropAreaHintStyled>
          <DropAreaPromptStyled>
            Drop your file here or <BrowseStyled>browse files</BrowseStyled>
          </DropAreaPromptStyled>
        </DropArea>
      )}
    </FileAreaStyled>
  </RootStyled>
);
